import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../api/axiosInstance";

interface SignupData {
  username: string;
  email: string;
  password: string;
  confirmPassword?: string; // Make optional since we won't send it
}

interface User {
  user_id: number;
  username: string;
  email: string;
  is_2fa_enabled: boolean;
}

interface AuthResponse {
  message?: string;
  is_2fa_enabled: boolean;
}

interface LoginData {
  username: string; // This field can contain either username or email
  password: string;
}

interface VerificationData {
  otp: string;
}

export const useSignup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: SignupData) => {
      // Remove confirmPassword before sending to server
      const { confirmPassword, ...dataToSend } = userData;
      // console.log("Sending signup data:", dataToSend);

      try {
        const res = await axios.post("/api/auth/signup", userData);
        // console.log("Signup response:", res.data);
        return res.data as AuthResponse;
      } catch (error: any) {
        // console.error(
        //   "Signup error details:",
        //   error.response?.data || error.message
        // );
        throw error;
      }
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful signup
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: LoginData) => {
      const res = await axios.post("/api/auth/login", userData, {
        withCredentials: true, // Required to send/receive cookies
      });
      return res.data as AuthResponse;
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful login
    },
  });
};

export const useEmailVerification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (verificationData: VerificationData) => {
      const res = await axios.post("/api/auth/verify-email", verificationData, {
        withCredentials: true,
      });
      return res.data as AuthResponse;
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful verification
    },
  });
};

export const useResendEmailVerification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.post("/api/auth/resend-otp");
      return res.data;
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful resend
    },
  });
};

export const useActivateAccount = (token: string) =>
  useQuery({
    queryKey: ["activate-account", token],
    enabled: !!token, // don’t run if token is falsy
    queryFn: async () => {
      const { data } = await axios.get(
        `/api/auth/activate?token=${encodeURIComponent(token)}`
      );
      return data; // whatever the backend returns
    },
    retry: false, // optional: no auto-retries on 4xx
    staleTime: Infinity, // keep result — avoids refetch
  });

export const useForgotPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const res = await axios.post("/api/auth/forgot-password", { email });
      return res.data;
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful password reset
    },
  });
};

export const useResetForgotPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      token: string;
      password: string;
      confirmPassword: string;
    }) => {
      const res = await axios.post("/api/auth/reset-forgot-password", data);
      return res.data;
    },
    onSuccess: () => {
      // Optionally invalidate queries or redirect after successful password reset
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        "/api/auth/logout",
        {},
        {
          withCredentials: true,
        }
      );
      return res.data;
    },
    onSuccess: () => {
      // Remove accessToken from localStorage on successful logout
      localStorage.removeItem("accessToken");
      console.log("[useLogout] AccessToken removed from localStorage");
      // Optionally invalidate queries or redirect after successful logout
    },
  });
};

// Mutation for resending activation link
export const useResendActivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const res = await axios.post("/api/auth/resend-activation", {
        email,
      });
      return res.data;
    },
    onSuccess: () => {},
  });
};

export function useAuthUser() {
  return useQuery({
    queryKey: ["authUser"],
    queryFn: async () => {
      const res = await axios.get("/api/auth/me", { withCredentials: true });
      return res.data.user;
    },
    retry: false, // No endless retry loop on 401
  });
}
